import React, { useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    useTheme,
    Box,
    Typography,
    LinearProgress,
    IconButton,
    Divider,
    CircularProgress
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";

import useFileUpload from "@/hooks/useFileUpload";

const BRAND_BLUE = {
    active: "#1954ED",
    activeBackground: "#F7F9FF"
} as const;

const BRAND_GREEN = "#008000";
const BRAND_GRAY = "#808080";

interface FileUploadVectorFileModalProps {
    isOpen: boolean;
    onClose: () => void;
    onUploadComplete?: (uploadedFileId?: number, uploadedFileName?: string) => void;
    fileName?: string; // Optional filename to process (for already uploaded files)
    file?: File; // Optional file to upload and process
}

const FileUploadVectorFileModal: React.FC<FileUploadVectorFileModalProps> = ({ isOpen, onClose, onUploadComplete, fileName, file }) => {
    const theme = useTheme();
    const [extractionStatus, setExtractionStatus] = React.useState<{ message: string; progress: number } | null>(null);
    const [hasStartedUpload, setHasStartedUpload] = React.useState(false);

    const { beginUpload, phase, isUploading, isExtracting, isSaving, isComplete, uploadProgress, currentFileName, error, reset } = useFileUpload({
        onUploadStart: () => {
            console.log("Processing started");
        },
        onUploadProgress: (progress: number) => {
            console.log(`Upload progress: ${progress}%`);
        },
        onUploadComplete: (fileName: string | undefined) => {
            console.log(`Upload complete: ${fileName}`);
        },
        onSavingFileToDatabaseStart: (fileName: string | undefined) => {
            console.log(`Saving to database: ${fileName}`);
        },
        onSavingFileToDatabaseComplete: (fileName: string | undefined, fileId: number) => {
            console.log(`Database save complete: ${fileName}, fileId: ${fileId}`);
            // Release MUI's modal focus trap and backdrop before the caller
            // opens the assignment dialog. Overlapping modal managers leave
            // the newer dialog visible but unable to receive pointer events.
            onClose();
            if (onUploadComplete) {
                onUploadComplete(fileId, fileName);
            }
        },
        onFileTextExtractionStart: (fileName: string) => {
            console.log(`Starting text extraction for ${fileName}`);
        },
        onFileTextExtractionProgress: (fileName: string, status: string, progress: number) => {
            console.log(`Extraction progress for ${fileName}: ${status} (${progress}%)`);
            setExtractionStatus({ message: status, progress });
        },
        onFileTextExtractionComplete: (fileName: string) => {
            console.log(`Text extraction completed for ${fileName}`);
            setExtractionStatus(null);
        },
        onError: (error: string) => {
            console.error(`File processing error: ${error}`);
            setExtractionStatus(null);
        },
        runVectorizer: true
    });

    // Track whether we've already processed this modal open
    const [isReadyToUpload, setIsReadyToUpload] = React.useState(false);

    // Reset when modal closes
    useEffect(() => {
        if (!isOpen) {
            reset();
            setExtractionStatus(null);
            setHasStartedUpload(false);
            setIsReadyToUpload(false);
        }
    }, [isOpen, reset]);

    // When modal opens, reset state first, then mark ready to upload
    useEffect(() => {
        if (isOpen && !hasStartedUpload && !isReadyToUpload) {
            // Reset the hook state to ensure we start fresh
            reset();
            setExtractionStatus(null);
            // Use a microtask to ensure state is updated before marking ready
            Promise.resolve().then(() => {
                setIsReadyToUpload(true);
            });
        }
    }, [isOpen, hasStartedUpload, isReadyToUpload, reset]);

    // Start processing only after we're ready (state has been reset)
    useEffect(() => {
        if (isOpen && isReadyToUpload && !hasStartedUpload) {
            if (file) {
                console.log("Starting upload for file:", file.name);
                setHasStartedUpload(true);
                beginUpload(file);
            } else if (fileName) {
                console.log("Starting upload for fileName:", fileName);
                setHasStartedUpload(true);
                beginUpload(fileName);
            }
        }
    }, [isOpen, fileName, file, beginUpload, hasStartedUpload, isReadyToUpload]);

    const handleCancel = () => {
        reset();
        setExtractionStatus(null);
        setHasStartedUpload(false);
        setIsReadyToUpload(false);
        onClose();
    };

    // Define process steps based on current phase
    const processSteps = [
        {
            id: "upload",
            title: "Processing Document",
            description: "Preparing document for analysis",
            isActive: isUploading || phase === "uploading",
            isComplete: phase !== "idle" && phase !== "uploading" && phase !== "error",
            showProgress: true
        },
        {
            id: "extract",
            title: "Extracting Text",
            description: "Reading and extracting text content from the document",
            isActive: isExtracting,
            isComplete: phase === "saving" || phase === "complete",
            showProgress: false
        },
        {
            id: "save",
            title: "Building Vector Index",
            description: "Creating searchable vector embeddings and saving to database",
            isActive: isSaving,
            isComplete: phase === "complete",
            showProgress: false
        }
    ];

    const getStepStatus = (step: (typeof processSteps)[0]) => {
        if (step.isComplete) return "complete";
        if (step.isActive) return "active";
        return "pending";
    };

    // Don't render the modal until we have a filename or file
    if (!fileName && !file) return null;

    return (
        <Dialog
            open={isOpen}
            onClose={(_, reason) => {
                if (reason === "backdropClick" || reason === "escapeKeyDown") {
                    return; // Prevent closing during processing
                }
                handleCancel();
            }}
            disableEscapeKeyDown
            PaperProps={{
                sx: {
                    borderRadius: "8px",
                    width: "500px",
                    maxWidth: "90vw",
                    boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.1)",
                    zIndex: theme.zIndex.modal + 1
                }
            }}
        >
            {/* Header */}
            <DialogTitle
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "24px",
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: "18px",
                    fontWeight: 600,
                    lineHeight: "24px",
                    color: "#1A1A1A",
                    paddingBottom: "16px"
                }}
            >
                Processing File
                <IconButton
                    onClick={handleCancel}
                    disabled={phase !== "idle" && phase !== "complete" && phase !== "error"}
                    sx={{
                        color: "#999999",
                        padding: "4px"
                    }}
                >
                    <CloseIcon sx={{ fontSize: "16px" }} />
                </IconButton>
            </DialogTitle>
            <Divider sx={{ borderColor: "#E5E5E5" }} />

            {/* Body */}
            <DialogContent
                sx={{
                    padding: "24px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "24px"
                }}
            >
                {/* Error Display */}
                {error && (
                    <Box
                        sx={{
                            backgroundColor: "#FEE2E2",
                            border: "1px solid #FCA5A5",
                            borderRadius: "8px",
                            padding: "12px",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px"
                        }}
                    >
                        <Typography sx={{ color: "#DC2626", fontSize: "14px" }}>⚠️ {error}</Typography>
                    </Box>
                )}

                {/* File Information */}
                <Box>
                    <Typography
                        sx={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: "14px",
                            fontWeight: 500,
                            color: "#666666",
                            marginBottom: "4px"
                        }}
                    >
                        Processing:
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: "16px",
                            fontWeight: 600,
                            color: "#1A1A1A",
                            wordBreak: "break-word"
                        }}
                    >
                        {currentFileName || file?.name || fileName}
                    </Typography>
                </Box>

                {/* Process Steps */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                    {processSteps.map(step => {
                        const status = getStepStatus(step);
                        return (
                            <Box
                                key={step.id}
                                sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: "12px",
                                    padding: "12px",
                                    borderRadius: "8px",
                                    backgroundColor: status === "active" ? BRAND_BLUE.activeBackground : "#F8F9FA",
                                    border: status === "active" ? `2px solid ${BRAND_BLUE.active}` : "1px solid #E5E5E5",
                                    transition: "all 0.3s ease"
                                }}
                            >
                                <Box
                                    sx={{
                                        width: "24px",
                                        height: "24px",
                                        borderRadius: "50%",
                                        backgroundColor: status === "complete" ? BRAND_GREEN : status === "active" ? BRAND_BLUE.active : BRAND_GRAY,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexShrink: 0,
                                        transition: "all 0.3s ease"
                                    }}
                                >
                                    {status === "complete" && <Typography sx={{ color: "white", fontSize: "14px", fontWeight: "bold" }}>✓</Typography>}
                                    {status === "active" && <CircularProgress size={14} sx={{ color: "white" }} />}
                                </Box>

                                <Box sx={{ flex: 1 }}>
                                    <Typography
                                        sx={{
                                            fontFamily: "'Poppins', sans-serif",
                                            fontSize: "14px",
                                            fontWeight: 600,
                                            color: status === "pending" ? "#999999" : "#1A1A1A",
                                            marginBottom: "4px"
                                        }}
                                    >
                                        {step.title}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontFamily: "'Poppins', sans-serif",
                                            fontSize: "12px",
                                            fontWeight: 400,
                                            color: "#666666"
                                        }}
                                    >
                                        {step.id === "extract" && extractionStatus ? extractionStatus.message : step.description}
                                    </Typography>

                                    {/* Progress bar for upload step */}
                                    {step.showProgress && status === "active" && (
                                        <Box sx={{ width: "100%", mt: "8px" }}>
                                            <LinearProgress
                                                variant="determinate"
                                                value={uploadProgress}
                                                sx={{
                                                    height: "6px",
                                                    borderRadius: "3px",
                                                    backgroundColor: "#E5E5E5",
                                                    "& .MuiLinearProgress-bar": {
                                                        backgroundColor: BRAND_BLUE.active,
                                                        borderRadius: "3px"
                                                    }
                                                }}
                                            />
                                            <Typography
                                                sx={{
                                                    fontFamily: "'Poppins', sans-serif",
                                                    fontSize: "11px",
                                                    fontWeight: 400,
                                                    color: "#666666",
                                                    marginTop: "4px"
                                                }}
                                            >
                                                {uploadProgress}% complete
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* Progress bar for extraction step */}
                                    {step.id === "extract" && status === "active" && extractionStatus && (
                                        <Box sx={{ width: "100%", mt: "8px" }}>
                                            <LinearProgress
                                                variant="determinate"
                                                value={extractionStatus.progress}
                                                sx={{
                                                    height: "6px",
                                                    borderRadius: "3px",
                                                    backgroundColor: "#E5E5E5",
                                                    "& .MuiLinearProgress-bar": {
                                                        backgroundColor: BRAND_BLUE.active,
                                                        borderRadius: "3px"
                                                    }
                                                }}
                                            />
                                            <Typography
                                                sx={{
                                                    fontFamily: "'Poppins', sans-serif",
                                                    fontSize: "11px",
                                                    fontWeight: 400,
                                                    color: "#666666",
                                                    marginTop: "4px"
                                                }}
                                            >
                                                {extractionStatus.progress}% complete
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        );
                    })}
                </Box>

                {/* Success Message */}
                {isComplete && (
                    <Box
                        sx={{
                            backgroundColor: "rgba(0, 128, 0, 0.08)",
                            border: "1px solid rgba(0, 128, 0, 0.3)",
                            borderRadius: "8px",
                            padding: "12px",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            marginTop: "16px"
                        }}
                    >
                        <Typography sx={{ color: BRAND_GREEN, fontSize: "14px", fontWeight: 500 }}>✅ File processed successfully!</Typography>
                    </Box>
                )}

                {/* Processing Note */}
                {(isUploading || isExtracting || isSaving) && (
                    <Typography
                        sx={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: "12px",
                            fontWeight: 400,
                            color: "#666666",
                            fontStyle: "italic",
                            textAlign: "center",
                            marginTop: "8px"
                        }}
                    >
                        This process may take several minutes depending on file size...
                    </Typography>
                )}
            </DialogContent>

            <Divider sx={{ borderColor: "#E5E5E5" }} />

            {/* Footer */}
            <DialogActions
                sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    padding: "16px 24px",
                    gap: "12px"
                }}
            >
                {(phase === "complete" || phase === "error") && (
                    <Button
                        onClick={handleCancel}
                        variant="contained"
                        sx={{
                            height: "36px",
                            padding: "0 16px",
                            borderRadius: "4px",
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: "14px",
                            fontWeight: 500,
                            backgroundColor: isComplete ? BRAND_GREEN : "#DC2626",
                            color: "white",
                            textTransform: "none",
                            "&:hover": {
                                backgroundColor: isComplete ? BRAND_GREEN : "#B91C1C"
                            }
                        }}
                    >
                        {isComplete ? "Done" : "Close"}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default FileUploadVectorFileModal;
